import { API_URL } from '../config';

export class PodcastService {
  /**
   * Get the latest podcast episode
   */
  static async getLatestEpisode() {
    try {
      const response = await fetch(`${API_URL}/podcast/latest-episode`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch latest episode');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching latest podcast episode:', error);
      return null;
    }
  }

  /**
   * Get recent podcast episodes
   */
  static async getRecentEpisodes(maxResults = 10) {
    try {
      const response = await fetch(`${API_URL}/podcast/recent-episodes?maxResults=${maxResults}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch recent episodes');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching recent podcast episodes:', error);
      return [];
    }
  }

  /**
   * Get podcast platform information
   */
  static async getPlatforms() {
    try {
      const response = await fetch(`${API_URL}/podcast/platforms`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch platform info');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching podcast platforms:', error);
      return [];
    }
  }

  /**
   * Get podcast service status
   */
  static async getServiceStatus() {
    try {
      const response = await fetch(`${API_URL}/podcast/status`);
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('Error fetching podcast service status:', error);
      return {
        configured: false,
        message: 'Service unavailable'
      };
    }
  }

  /**
   * Get platform icon component name
   */
  static getPlatformIcon(platform) {
    const iconMap = {
      apple: 'FaApple',
      podcastaddict: 'SiPodcastaddict',
      podbean: 'FaPodcast'
    };
    return iconMap[platform] || 'FaPodcast';
  }

  /**
   * Get platform display name
   */
  static getPlatformName(platform) {
    const nameMap = {
      apple: 'Apple Podcasts',
      podcastaddict: 'Podcast Addict',
      podbean: 'Podbean'
    };
    return nameMap[platform] || 'Podcast';
  }

  /**
   * Format published date
   */
  static formatPublishedDate(publishedAt) {
    if (!publishedAt) return 'Unknown';
    
    const date = new Date(publishedAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  /**
   * Get platform-specific URL
   */
  static getPlatformUrl(platform, platformId) {
    const urlMap = {
      apple: `https://podcasts.apple.com/us/podcast/id${platformId}`,
      podcastaddict: `https://podcastaddict.com/podcast/${platformId}`,
      podbean: `https://www.podbean.com/podcast-detail/${platformId}`
    };
    return urlMap[platform] || '#';
  }
}
